import * as React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
} from '@mui/material';

interface DeleteConfirmationDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title?: string;
  message?: string;
  confirmText?: string;
  cancelText?: string;
  /** When set, user must type this text to enable the confirm button (case-insensitive). */
  confirmRequiredText?: string;
  confirmLoading?: boolean;
}

export const DeleteConfirmationDialog = ({
  open,
  onClose,
  onConfirm,
  title = 'Delete Configuration?',
  message = 'Are you sure you want to delete this configuration? This action cannot be undone.',
  confirmText = 'Delete',
  cancelText = 'Cancel',
  confirmRequiredText,
  confirmLoading = false,
}: DeleteConfirmationDialogProps) => {
  const [confirmInput, setConfirmInput] = React.useState('');

  const isConfirmEnabled = confirmRequiredText
    ? confirmInput.trim().toLowerCase() === confirmRequiredText.toLowerCase()
    : true;

  const handleClose = () => {
    if (confirmLoading) return;
    setConfirmInput('');
    onClose();
  };

  const handleConfirm = () => {
    if (!isConfirmEnabled || confirmLoading) return;
    setConfirmInput('');
    onConfirm();
  };

  React.useEffect(() => {
    if (!open) setConfirmInput('');
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="delete-dialog-title"
      aria-describedby="delete-dialog-description"
    >
      <DialogTitle id="delete-dialog-title">{title}</DialogTitle>
      <DialogContent>
        <DialogContentText id="delete-dialog-description">
          {message}
        </DialogContentText>
        {confirmRequiredText && (
          <TextField
            fullWidth
            size="small"
            label={`Type "${confirmRequiredText}" to confirm`}
            value={confirmInput}
            onChange={e => setConfirmInput(e.target.value)}
            sx={{ mt: 2 }}
            autoComplete="off"
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="secondary" disabled={confirmLoading}>
          {cancelText}
        </Button>
        <Button
          onClick={handleConfirm}
          color="primary"
          autoFocus={!confirmRequiredText}
          disabled={!isConfirmEnabled || confirmLoading}
          startIcon={confirmLoading ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
